/**
 * Route Royal Page Content Component
 * 
 * Full page content display with clean, professional styling
 */

'use client';

import type { PageContentProps } from '@routeroyal/theme-core';
import { MarkdownRenderer, useMarkdownHeadings } from '@routeroyal/ui-core';
import { RouteRoyalPageHeader } from './RouteRoyalPageHeader';
import { RouteRoyalPageTableOfContents } from './RouteRoyalPageTableOfContents';
import Link from 'next/link';
import { useEffect, useState } from 'react';

/**
 * RouteRoyalPageContent - Route Royal-themed full content display for static pages
 * 
 * Renders HTML/markdown content with rich formatting, table of contents,
 * and last updated information with clean blue styling.
 */
export function RouteRoyalPageContent({
  page,
  showTableOfContents = page.showTableOfContents ?? true,
  showLastUpdated = page.showLastUpdated ?? true,
  showPrintButton = false,
  locale = 'en',
  className = '',
  messages,
}: PageContentProps) {
  const [activeHeadingId, setActiveHeadingId] = useState<string>();
  const [linkCopied, setLinkCopied] = useState(false);

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Extract headings from content for TOC using the core hook
  // Pages might be HTML or Markdown format
  const allHeadings = useMarkdownHeadings(page.content, 'MARKDOWN');
  const headings = showTableOfContents ? allHeadings : [];

  // Track active heading for TOC
  useEffect(() => {
    if (!showTableOfContents || headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeadingId(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -80% 0px' }
    );

    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [headings, showTableOfContents]);

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Handle copy link
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <article className={`max-w-4xl mx-auto ${className}`}>
      {/* Header */}
      <RouteRoyalPageHeader
        title={page.title}
        {...(page.excerpt && { excerpt: page.excerpt })}
        {...(page.featuredImage && { featuredImage: page.featuredImage })}
        showBreadcrumb
        breadcrumbItems={[
          { label: messages?.backToHome || 'Home', href: '/' },
          { label: page.title },
        ]}
      />

      {/* Meta Information */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mt-6 mb-8 pb-6 border-b border-gray-200">
        {/* Page Type Badge */}
        {page.type && (
          <span className={`
            inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide
            ${page.type === 'LEGAL' 
              ? 'bg-gray-100 text-gray-700' 
              : page.type === 'INFO'
              ? 'bg-blue-50 text-blue-700'
              : 'bg-gray-100 text-gray-600'
            }
          `}>
            {page.type === 'LEGAL' && (
              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            )}
            {page.type === 'INFO' && (
              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            {page.type}
          </span>
        )}

        {/* Last Updated */}
        {showLastUpdated && page.updatedAt && (
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-blue-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="font-medium">
              {messages?.lastUpdated || 'Last updated'}: {formatDate(page.updatedAt)}
            </span>
          </div>
        )}

        {/* Reading Time */}
        {page.readingTime && (
          <>
            <span className="hidden sm:block w-px h-5 bg-gray-300" />
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
              <span className="font-medium">
                {page.readingTime} {messages?.minuteRead || 'min read'}
              </span>
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 ml-auto">
          {/* Copy Link Button */}
          <button
            onClick={handleCopyLink}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-blue-50 text-gray-600 hover:text-blue-700 transition-colors text-sm font-medium"
          >
            {linkCopied ? (
              <>
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-green-600">{messages?.linkCopied || 'Copied!'}</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                <span>{messages?.copyLink || 'Copy link'}</span>
              </>
            )}
          </button>

          {/* Print Button */}
          {showPrintButton && (
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors text-sm font-medium"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              <span>{messages?.printPage || 'Print'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className={`relative ${showTableOfContents && headings.length > 0 ? 'lg:grid lg:grid-cols-[1fr_260px] lg:gap-8' : ''}`}>
        {/* Table of Contents (Desktop) */}
        {showTableOfContents && headings.length > 0 && (
          <aside className="hidden lg:block lg:order-2">
            <div className="sticky top-24">
              <RouteRoyalPageTableOfContents
                headings={headings}
                {...(activeHeadingId && { activeId: activeHeadingId })}
                title={messages?.onThisPage || 'On this page'}
              />
            </div>
          </aside>
        )}

        {/* Page Content */}
        <div className="lg:order-1">
          <MarkdownRenderer
            content={page.content}
            contentFormat="MARKDOWN"
            theme="route-royal"
          />
        </div>
      </div>

      {/* Footer Section for Legal Pages */}
      {page.type === 'LEGAL' && (
        <div className="mt-16 pt-8 border-t border-gray-200">
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-3 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Questions About This Policy?</h3>
                <p className="text-gray-600 mb-4">
                  If you have any questions about this document or would like further clarification, please contact us.
                </p>
                <Link
                  href={`/${locale}/contact`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Contact Us
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}
